/*
  STIMULATE — Run Club page behaviour.

  Two jobs only, both progressive enhancements. Every one of them can fail and
  the page still converts:
    1. forward a new member to Strava after the lead is saved
    2. smooth-scroll the in-page CTA anchors

  The Strava link is a real <a> in the markup and the anchors are real hrefs,
  so with JavaScript disabled or broken the visitor simply clicks instead of
  being carried. Nothing here is load-bearing.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  /* --- 1. Post-signup Strava handoff --------------------------------- */

  function initStravaHandoff(root) {
    var url = root.getAttribute('data-strava-url');
    if (!url) return; // No club URL configured — the markup already shows a holding message.

    // Only ever forward to an absolute http(s) destination. A relative or
    // javascript: value here would be both broken and an injection vector.
    var parsed;
    try {
      parsed = new URL(url, window.location.origin);
    } catch (e) {
      return;
    }
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return;

    var delay = parseInt(root.getAttribute('data-redirect-delay'), 10);
    if (isNaN(delay) || delay <= 0) return; // 0 = auto-forward disabled by the merchant.

    var countdown = root.querySelector('[data-stimulate-countdown]');
    var remaining = delay;
    var cancelled = false;

    // Any deliberate interaction cancels the forward. Someone who has started
    // reading, or clicked the button themselves, should not have the page
    // yanked out from under them.
    function cancel() {
      if (cancelled) return;
      cancelled = true;
      window.clearInterval(timer);
      if (countdown) countdown.hidden = true;
    }

    var link = root.querySelector('[data-stimulate-strava-link]');
    if (link) link.addEventListener('click', cancel);
    root.addEventListener('keydown', function (e) { if (e.key === 'Escape') cancel(); });

    function render() {
      if (!countdown) return;
      countdown.hidden = false;
      countdown.textContent =
        'Taking you to Strava in ' + remaining + ' second' + (remaining === 1 ? '' : 's') + '…';
    }

    render();

    var timer = window.setInterval(function () {
      remaining -= 1;
      if (cancelled) return;
      if (remaining <= 0) {
        window.clearInterval(timer);
        // Same-tab navigation: the member is finished with this page, and a
        // popup would likely be blocked since this is not a click handler.
        window.location.href = parsed.href;
        return;
      }
      render();
    }, 1000);
  }

  /* --- 1b. Hero background video ------------------------------------- */

  /*
    Attaches exactly ONE source URL, chosen at runtime.

    This cannot be done in markup. <source media="..."> is only honoured
    inside <picture>; inside <video> browsers ignore it and play the first
    decodable source, so declarative markup would download the wrong
    rendition and would never honour prefers-reduced-motion. Choosing here is
    what keeps it to a single download.

    Every failure path ends the same way: no source attached, video stays at
    opacity 0, poster image remains visible. That covers reduced motion, a
    missing URL, a refused autoplay, and a decode error.
  */
  function initHeroVideo(video) {
    if (video.dataset.stimulateVideoReady === '1') return; // Editor re-render guard.

    if (reduceMotion) return; // Poster only. Never attach, never download.

    var isMobile = window.matchMedia
      ? window.matchMedia('(max-width: 749px)').matches
      : false;

    var desktop = video.getAttribute('data-src-desktop');
    var mobile = video.getAttribute('data-src-mobile');
    var src = isMobile ? (mobile || desktop) : (desktop || mobile);
    if (!src) return;

    video.dataset.stimulateVideoReady = '1';

    function reveal() {
      video.classList.add('is-playing');
    }

    // 'playing' is the only event that means pixels are actually moving.
    // 'canplay' can fire on a frame that never advances.
    video.addEventListener('playing', reveal, { once: true });
    video.addEventListener('error', function () {
      video.classList.remove('is-playing');
    });

    video.src = src;
    video.load();

    // Autoplay can still be refused (low-power mode, data saver, platform
    // policy). A rejected promise is a normal outcome, not an error worth
    // surfacing — the poster is already the right thing to show.
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () { /* poster stays */ });
    }
  }

  /* --- 2. Smooth in-page navigation ---------------------------------- */

  function initAnchors() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('a[href^="#"]') : null;
      if (!link) return;

      var id = link.getAttribute('href');
      if (!id || id === '#') return;

      var target;
      try {
        target = document.querySelector(id);
      } catch (err) {
        return; // Not a valid selector — let the browser deal with it.
      }
      if (!target) return; // Section not on this page; native behaviour applies.

      e.preventDefault();
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });

      // scrollIntoView alone does not move keyboard focus, which would strand
      // a keyboard or screen-reader user at the top of the document while the
      // page visually jumped elsewhere.
      var hadTabindex = target.hasAttribute('tabindex');
      if (!hadTabindex) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      if (!hadTabindex) {
        target.addEventListener('blur', function handler() {
          target.removeAttribute('tabindex');
          target.removeEventListener('blur', handler);
        });
      }

      // Keep the URL shareable without triggering a second jump.
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', id);
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-stimulate-success]').forEach(initStravaHandoff);
    document.querySelectorAll('[data-stimulate-hero-video]').forEach(initHeroVideo);
    initAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Both section files load this script, and the Theme Editor re-renders
  // sections without a page reload. Re-running the handoff on that event keeps
  // the success state working while an editor is previewing it.
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-stimulate-success]').forEach(initStravaHandoff);
    e.target.querySelectorAll('[data-stimulate-hero-video]').forEach(initHeroVideo);
  });
})();
